import { useCallback, useEffect, useRef } from 'react';
import { type KeyedMutator } from 'swr';

import { ClockReminderOutreachDetail } from '@apps/web/components/outreaches/clock_reminder_outreach_detail';
import { OutreachDetail } from '@apps/web/components/outreaches/outreach_detail';
import { TrainingModePageTourScope } from '@apps/web/components/page_tour/training_mode_page_tour';
import { OUTREACH_DETAIL_PAGE_TOUR_STEPS } from '@apps/web/components/page_tour/training_mode_tour_configs';
import { ensureOutreachRunnerRun } from '@apps/web/components/phoebe_chat_view/conversation/chat_backend_adapter';
import { useOrganizationContext } from '@apps/web/context/organization_provider';
import { useUI } from '@apps/web/context/ui_provider';
import { useListenForOrganizationEvent } from '@apps/web/hooks/use_listen_for_organization_event';
import { useIsMobile } from '@apps/web/hooks/use_mobile';
import { type OrganizationEvent } from '@apps/web/hooks/use_organization_events';
import { useOrganizationReconnectRecovery } from '@apps/web/hooks/use_organization_reconnect_recovery';
import {
    type ChatTypingUser,
    usePhoebeChatTyping,
} from '@apps/web/hooks/use_phoebe_chat_typing';
import { useResourceOrganizationLookup } from '@apps/web/hooks/use_resource_organization_lookup';
import { useThrottledCallback } from '@apps/web/hooks/use_throttled_callback';
import { toast } from '@apps/web/lib/app_notifications';
import { organizationEventReferencesOutreach } from '@apps/web/lib/organization_event_resources';
import {
    OPEN_OUTREACH_CHAT_EVENT,
    type OutreachChatOpenedDetail,
} from '@apps/web/lib/outreach_chat_open_event';
import { isClockReminderOutreach } from '@apps/web/lib/outreach_utils';
import {
    useJoinedOutreach,
    useOutreachAnalysisConversationByOutreach,
    useOutreachAnalysisRunByOutreach,
    useOutreachRunnerRunByOutreach,
    useUser,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    ApiError,
    type JoinedOutreachRead,
} from '@libraries/typescript/api_client';

const OUTREACH_EVENT_REFRESH_THROTTLE_MS = 5_000;
const OUTREACH_UPDATED_REFRESH_THROTTLE_MS = 3_000;

const NO_CHAT_TYPING_USERS: Array<ChatTypingUser> = [];

type OutreachDetailPageProps = {
    readonly autoOpenChatOnLoad?: boolean;
    readonly chatId?: string | null;
    readonly chatMode?: 'fullscreen';
    readonly outreachAction?: 'assign_and_fill';
    readonly outreachId: string;
    readonly organizationId?: string | null;
    readonly selectedContactAttemptId?: string | null;
    readonly suppressAutoOpenChat?: boolean;
};

export function OutreachDetailPage({
    autoOpenChatOnLoad = false,
    chatId,
    chatMode,
    outreachAction,
    outreachId,
    organizationId: organizationIdOverride,
    selectedContactAttemptId,
    suppressAutoOpenChat = false,
}: OutreachDetailPageProps) {
    const pageOwnsChatLifecycle = !suppressAutoOpenChat;
    const { organizationId: contextOrganizationId } = useOrganizationContext();
    const organizationId = organizationIdOverride ?? contextOrganizationId;
    const {
        autoOpenChat,
        clearAutoOpenSuppression,
        closeAutoOpenedChat,
        clearRequestedChatId,
        isChatVisible,
        openChatWithId,
    } = useUI();
    const isMobile = useIsMobile();
    const hasAutoOpenedChatForOutreachRef = useRef(false);

    const {
        data: outreach,
        isLoading,
        error,
        mutate: mutateOutreach,
    } = useJoinedOutreach(organizationId, outreachId, {
        reason: 'outreach-detail-page',
    });

    // A v3-born outreach is always chat-backed: its runner either exists or
    // is claimed the moment someone opens the chat.
    const isV3BornOutreach = outreach?.agent_generation === 'v3';
    const isAgentBackedOutreach =
        isV3BornOutreach ||
        (outreach?.agent_status !== null &&
            outreach?.agent_status !== undefined);
    const isOutreachActive = outreach?.agent_status === 'analyzing';
    const isMissingOutreachInCurrentOrganization =
        error instanceof ApiError && error.status === 404;

    const { isResolvingAcrossOrganizations } = useResourceOrganizationLookup({
        enabled: isMissingOutreachInCurrentOrganization,
        resourceId: outreachId,
        resourceType: 'outreach',
    });

    // Generation is only knowable once the outreach payload lands, so none of
    // these ask by-resource before then — a guess would 404 on every load.
    const isOutreachLoaded = outreach !== undefined;
    const legacyChatOutreachId =
        isOutreachLoaded && isAgentBackedOutreach && !isV3BornOutreach
            ? outreachId
            : null;
    const runnerOutreachId =
        isOutreachLoaded && isV3BornOutreach ? outreachId : null;
    const { data: outreachConversation, mutate: mutateOutreachConversation } =
        useOutreachAnalysisConversationByOutreach(
            organizationId,
            legacyChatOutreachId,
        );
    const { data: outreachAnalysisRun, mutate: mutateOutreachAnalysisRun } =
        useOutreachAnalysisRunByOutreach(organizationId, legacyChatOutreachId);
    const { data: outreachRunnerRun, mutate: mutateOutreachRunnerRun } =
        useOutreachRunnerRunByOutreach(organizationId, runnerOutreachId);
    const outreachRun = isV3BornOutreach
        ? outreachRunnerRun
        : outreachAnalysisRun;
    const mutateOutreachRun = isV3BornOutreach
        ? mutateOutreachRunnerRun
        : mutateOutreachAnalysisRun;
    const outreachChatId = outreachRun?.id ?? outreachConversation?.id ?? null;

    const { data: user } = useUser({ reason: 'outreach-detail-page' });
    // Receive-only presence: a teammate typing in this outreach's chat should
    // be visible from the detail page too. `enabled` gates only outbound
    // typing pings (this page has no composer); the listener always runs.
    const { typingUsers: outreachChatTypingUsers } = usePhoebeChatTyping({
        organizationId,
        runId: outreachRun?.id ?? null,
        currentUserId: user?.id ?? null,
        enabled: false,
    });
    // The docked chat renders its own typing indicator above the composer, so
    // the page-bottom pill only shows while the chat panel is hidden.
    const chatTypingUsers = isChatVisible
        ? NO_CHAT_TYPING_USERS
        : outreachChatTypingUsers;

    // The joined outreach fans out to shifts, attempts and responses, so a
    // burst of writes collapses into one leading and one trailing refetch.
    const refreshOutreach = useCallback(() => {
        void mutateOutreach();
    }, [mutateOutreach]);
    const throttledRefreshOutreach = useThrottledCallback(
        refreshOutreach,
        OUTREACH_UPDATED_REFRESH_THROTTLE_MS,
    );
    const handleOutreachUpdated = useCallback(
        (event: OrganizationEvent) => {
            if (!organizationEventReferencesOutreach(event, outreachId)) {
                return;
            }
            throttledRefreshOutreach();
        },
        [outreachId, throttledRefreshOutreach],
    );
    useEffect(
        () => throttledRefreshOutreach.cancel,
        [outreachId, throttledRefreshOutreach],
    );

    // When Phoebe (or a Slack button) changes this outreach, the backend
    // broadcasts an `outreach_updated` event. Refetch so the status badge,
    // shift rows and metadata reflect the change immediately, without the
    // coordinator needing to reload the page.
    useListenForOrganizationEvent('outreach_updated', handleOutreachUpdated, {
        organizationId,
    });

    // The agent runtime publishes outreach_suggestion_added on every run
    // state sync, which covers the kickoff gap before the analysis run and
    // conversation rows are linked. Use it to discover the chat link
    // without fast polling; event bursts collapse into one leading and one
    // trailing refresh per throttle window.
    const refreshOutreachChatLink = useCallback(() => {
        void mutateOutreachConversation();
        void mutateOutreachRun();
    }, [mutateOutreachConversation, mutateOutreachRun]);
    const throttledRefreshOutreachChatLink = useThrottledCallback(
        refreshOutreachChatLink,
        OUTREACH_EVENT_REFRESH_THROTTLE_MS,
    );
    const handleOutreachAnalysisProgress = useCallback(
        (event: OrganizationEvent) => {
            if (event.resource.id !== outreachId || outreachChatId !== null) {
                return;
            }
            throttledRefreshOutreachChatLink();
        },
        [outreachChatId, outreachId, throttledRefreshOutreachChatLink],
    );
    useEffect(
        () => throttledRefreshOutreachChatLink.cancel,
        [outreachId, throttledRefreshOutreachChatLink],
    );
    useListenForOrganizationEvent(
        'outreach_suggestion_added',
        handleOutreachAnalysisProgress,
        { organizationId },
    );

    const isOutreachChatLinkPending =
        isOutreachActive && outreachChatId === null;

    // Events published while the socket was down are never redelivered, so a
    // reconnect has to re-read the outreach and its chat link.
    const recoverOutreachPage = useCallback(async () => {
        if (isOutreachChatLinkPending) {
            refreshOutreachChatLink();
        }
        await mutateOutreach();
    }, [isOutreachChatLinkPending, mutateOutreach, refreshOutreachChatLink]);
    useOrganizationReconnectRecovery({
        organizationId,
        recoveryLabel: 'outreach-detail-page',
        priority: 35,
        recover: recoverOutreachPage,
    });

    // Safety net for the chat-link discovery above: a dropped websocket or
    // missed event must not strand the page without its chat link, so keep
    // a slow refresh until the link exists.
    useEffect(() => {
        if (!isOutreachChatLinkPending) {
            return;
        }
        const id = window.setInterval(() => {
            void mutateOutreachConversation();
            void mutateOutreachRun();
        }, 30_000);
        return () => window.clearInterval(id);
    }, [
        isOutreachChatLinkPending,
        mutateOutreachConversation,
        mutateOutreachRun,
    ]);

    useEffect(() => {
        if (outreachChatId !== null) {
            void mutateOutreach();
        }
    }, [mutateOutreach, outreachChatId]);

    useEffect(() => {
        if (!chatId) {
            return;
        }
        clearAutoOpenSuppression();
        if (chatMode) {
            openChatWithId(chatId, { mode: chatMode });
            return;
        }
        openChatWithId(chatId);
    }, [chatId, chatMode, clearAutoOpenSuppression, openChatWithId]);

    const handleOpenOutreachChat = useCallback(() => {
        clearAutoOpenSuppression();
        if (outreachChatId !== null) {
            openChatWithId(outreachChatId);
            return;
        }
        autoOpenChat();
        if (!isV3BornOutreach || organizationId === null) {
            return;
        }
        // A v3-born outreach without a runner gets one on open. A failure
        // here leaves an empty chat shell, so say so and offer the retry
        // instead of letting the panel sit there silently.
        void ensureOutreachRunnerRun({ organizationId, outreachId })
            .then(async (run) => {
                await mutateOutreachRunnerRun(run, { revalidate: false });
                if (run?.id) {
                    openChatWithId(run.id);
                }
            })
            .catch(() => {
                toast.error('Could not start Phoebe on this outreach.', {
                    persistNotification: false,
                    action: {
                        label: 'Try again',
                        onClick: () => handleOpenOutreachChatRef.current?.(),
                    },
                });
            });
    }, [
        autoOpenChat,
        clearAutoOpenSuppression,
        isV3BornOutreach,
        mutateOutreachRunnerRun,
        openChatWithId,
        organizationId,
        outreachChatId,
        outreachId,
    ]);
    const handleOpenOutreachChatRef = useRef(handleOpenOutreachChat);
    useEffect(() => {
        handleOpenOutreachChatRef.current = handleOpenOutreachChat;
    }, [handleOpenOutreachChat]);
    const outreachChatIdRef = useRef(outreachChatId);
    useEffect(() => {
        outreachChatIdRef.current = outreachChatId;
    }, [outreachChatId]);

    // The shell's launcher and mobile Chat tab open the panel directly, so
    // a v3-born outreach with no runner would show an empty chat. v2-born
    // outreaches ignore this: their panel already resolves on its own.
    useEffect(() => {
        if (!isV3BornOutreach || typeof window === 'undefined') {
            return undefined;
        }
        const handleShellOpenedChat = (event: Event) => {
            const detail = (event as CustomEvent<OutreachChatOpenedDetail>)
                .detail;
            if (detail?.outreachId && detail.outreachId !== outreachId) {
                return;
            }
            if (outreachChatIdRef.current !== null) {
                return;
            }
            handleOpenOutreachChatRef.current?.();
        };
        window.addEventListener(
            OPEN_OUTREACH_CHAT_EVENT,
            handleShellOpenedChat,
        );
        return () => {
            window.removeEventListener(
                OPEN_OUTREACH_CHAT_EVENT,
                handleShellOpenedChat,
            );
        };
    }, [isV3BornOutreach, outreachId]);

    // Auto-open chat once the agent conversation is available. Explicit
    // create-flow requests can show the outreach-targeted chat earlier; the
    // chat view owns its loading state while the conversation is created.
    // isChatVisible is intentionally excluded from deps: autoOpenChat
    // already guards against opening when visible, and including it
    // causes a feedback loop where closing the chat re-triggers this
    // effect and immediately re-opens it.
    useEffect(() => {
        if (!pageOwnsChatLifecycle) {
            return;
        }
        if (hasAutoOpenedChatForOutreachRef.current) {
            return;
        }
        if (autoOpenChatOnLoad) {
            if (!isAgentBackedOutreach) {
                return;
            }
            // An explicit open request on a runner-less v3-born outreach has
            // to claim one, or the panel opens onto nothing. The ref above
            // keeps this to one attempt per outreach; a failure surfaces the
            // retry toast rather than looping on the async SWR update.
            if (isV3BornOutreach && outreachChatId === null) {
                handleOpenOutreachChatRef.current?.();
                hasAutoOpenedChatForOutreachRef.current = true;
                return;
            }
            clearAutoOpenSuppression();
            autoOpenChat();
            hasAutoOpenedChatForOutreachRef.current = true;
            return;
        }
        if (isMobile || outreachChatId === null) {
            return;
        }
        autoOpenChat();
        hasAutoOpenedChatForOutreachRef.current = true;
    }, [
        autoOpenChat,
        autoOpenChatOnLoad,
        clearAutoOpenSuppression,
        isAgentBackedOutreach,
        isMobile,
        isV3BornOutreach,
        outreachChatId,
        pageOwnsChatLifecycle,
    ]);

    // Close auto-opened chat when switching to an outreach with no agent
    // conversation. For non-agent outreaches `outreachConversation` is
    // always null (the hook is disabled), so we key off that.
    // Guard on `outreach` to skip the initial loading state.
    useEffect(() => {
        if (!pageOwnsChatLifecycle || !outreach || isAgentBackedOutreach) {
            return;
        }
        closeAutoOpenedChat();
    }, [
        closeAutoOpenedChat,
        isAgentBackedOutreach,
        outreach,
        pageOwnsChatLifecycle,
    ]);

    useEffect(() => {
        hasAutoOpenedChatForOutreachRef.current = false;
    }, [outreachId]);

    useEffect(() => {
        if (!pageOwnsChatLifecycle || !outreach || isAgentBackedOutreach) {
            return;
        }
        clearRequestedChatId();
    }, [
        clearRequestedChatId,
        isAgentBackedOutreach,
        outreach,
        pageOwnsChatLifecycle,
    ]);

    if (isLoading || isResolvingAcrossOrganizations) {
        return (
            <div className="flex justify-center p-6">
                Loading outreach details…
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-destructive flex justify-center p-6">
                Error loading outreach: {error.message}
            </div>
        );
    }

    if (!outreach) {
        return (
            <div className="flex justify-center p-6">
                Outreach not found. It may have been deleted.
            </div>
        );
    }

    if (isClockReminderOutreach(outreach.outreach_type)) {
        return (
            <ClockReminderOutreachDetail
                outreach={outreach}
                mutateOutreach={mutateOutreach}
                onOpenOutreachChat={
                    isAgentBackedOutreach ? handleOpenOutreachChat : undefined
                }
                isOutreachChatAvailable={outreachChatId !== null}
                selectedContactAttemptId={selectedContactAttemptId}
            />
        );
    }

    return (
        <TrainingModePageTourScope
            pageId="outreach-detail"
            organizationId={organizationId}
            ready={true}
            steps={OUTREACH_DETAIL_PAGE_TOUR_STEPS}
        >
            <OutreachDetail
                key={outreach.id}
                outreachAction={outreachAction}
                outreach={outreach}
                mutateOutreach={mutateOutreach}
                chatTypingUsers={chatTypingUsers}
                onOpenOutreachChat={
                    isAgentBackedOutreach ? handleOpenOutreachChat : undefined
                }
                isOutreachChatAvailable={outreachChatId !== null}
                selectedContactAttemptId={selectedContactAttemptId}
            />
        </TrainingModePageTourScope>
    );
}

export type OutreachDetailProps = {
    readonly outreach: JoinedOutreachRead;
    readonly mutateOutreach: KeyedMutator<JoinedOutreachRead>;
    readonly selectedContactAttemptId?: string | null;
};
